import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Pin } from "lucide-react";
import { toast } from "sonner";
import MessageItem from "@/components/community/MessageItem";
import Composer from "@/components/community/Composer";
import PinnedPanel from "@/components/community/PinnedPanel";

const merge = (prev, latest) => {
  if (!latest.length) return [];
  const first = latest[0].created_at;
  return [...prev.filter((m) => m.created_at < first), ...latest];
};

export default function RoomView({ api, room, me }) {
  const [msgs, setMsgs] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinTick, setPinTick] = useState(0);
  const endRef = useRef(null);
  const base = `/community/rooms/${room.id}`;

  const refresh = useCallback(async (scroll) => {
    try {
      const { data } = await api.get(`${base}/messages`);
      setMsgs((p) => (p ? merge(p, data.messages) : data.messages));
      setHasMore((h) => h || data.has_more);
      if (scroll) setTimeout(() => endRef.current?.scrollIntoView({ block: "end" }), 50);
    } catch {
      setMsgs((p) => p || []);
    }
  }, [api, base]);

  useEffect(() => {
    refresh(true);
    const t = setInterval(() => refresh(false), 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const loadEarlier = async () => {
    const { data } = await api.get(`${base}/messages`, { params: { before: msgs[0].created_at } });
    setMsgs((p) => [...data.messages, ...p]);
    setHasMore(data.has_more);
  };

  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      setPinTick((n) => n + 1);
      refresh(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Try again.");
    }
  };

  return (
    <section className="brutal-border flex flex-col min-h-[60vh] min-w-0" data-testid="community-room-view">
      <header className="px-4 py-3 border-b border-[#262626] flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-heading font-black text-lg truncate" data-testid="community-room-title"># {room.label}</h2>
          <p className="text-xs text-muted-foreground truncate">{room.desc}</p>
        </div>
        <button onClick={() => setPinOpen((v) => !v)} data-testid="community-pinned-toggle" aria-pressed={pinOpen}
          className={`min-h-[40px] px-3 border font-mono-label text-[11px] inline-flex items-center gap-2 shrink-0 transition-colors ${pinOpen ? "border-[#FFD600] text-[#FFD600]" : "border-[#262626] hover:border-white"}`}>
          <Pin size={13} /> Resources
        </button>
      </header>
      {pinOpen && <PinnedPanel api={api} base={base} tick={pinTick} me={me} />}
      <div className="flex-1 overflow-y-auto max-h-[60vh] px-2 sm:px-4 py-3 space-y-1" data-testid="community-message-list">
        {msgs === null && <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>}
        {hasMore && msgs?.length > 0 && (
          <button onClick={loadEarlier} data-testid="community-load-earlier" className="w-full py-2 text-xs text-muted-foreground hover:text-white">Load earlier messages</button>
        )}
        {msgs?.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center" data-testid="community-empty">No messages yet. Start the conversation.</p>}
        {msgs?.map((m) => <MessageItem key={m.id} api={api} msg={m} me={me} act={act} />)}
        <div ref={endRef} />
      </div>
      <Composer api={api} base={base} disabled={me.muted} onSent={() => refresh(true)} />
    </section>
  );
}
